import { useFormField } from '@calendly/form'
import { array, object } from '@calendly/form/validation'
import { pick } from 'lodash-es'
import { useCallback } from 'react'

import { encodeAvailabilityRule, getDefaultRules, getOwnRuleId } from 'components/availability/form/utils'
import {
  DEFAULT_RULE_ID,
  determineMembers,
  getAvailabilityFromRule,
  getBookingRangeInitialValues,
  getBookingRangeValidationSchema,
  getMaxBookingTime,
  getMemberData,
  getMinBookingTime,
  getNoticePeriodInitialValue,
  getNoticePeriodValidationSchema,
  membersToServerDataV3
} from 'components/event_type_edit'
import { getPeriodType } from 'components/event_type_edit/booking_range/get_period_type'

import { areAvailableHoursValid } from './available_hours/components/utils'
import { findAvailabilityMembership } from './helpers'
import { useGetDefaultAvailabilityRules } from '../../../../utils'
import { filterEnabledMemberships } from '../helpers'

const getMemberAttributes = (member) => {
  const {
    id,
    userUuid,
    userId,
    memberId,
    availableHours = [],
    dateOverrides,
    timezone,
    enabled,
    deleted,
    name,
    usingCustomHours,
    customRuleId,
    defaultHours = {},
    organizationInvitationId,
    organizationInvitationUuid,
    isPending,
    usingSecondarySchedule,
    activeAvailabilityRuleId,
    primaryRule,
    holidays,
    userHolidaySet,
    ownerAvailabilityRules
  } = member

  const { availableHours: defaultAvailableHours = [], timezone: defaultTimezone } = defaultHours

  const attributes = {
    id,
    userId,
    userUuid,
    enabled,
    memberId,
    _destroy: deleted,
    timezone: timezone || defaultTimezone,
    usingCustomHours,
    customRuleId,
    dateOverrides,
    primaryRule,
    activeAvailabilityRuleId,
    name,
    organizationInvitationId,
    organizationInvitationUuid,
    isPending,
    availableHours: availableHours.length === 0 ? defaultAvailableHours : availableHours,
    holidays,
    userHolidaySet,
    usingSecondarySchedule,
    ownerAvailabilityRules
  }
  if (!String(memberId).startsWith('NEW')) {
    attributes.id = memberId
  }

  return attributes
}

const buildMembershipsData = (members) => {
  const membersData = Object.values(members)

  return {
    event_type_memberships_attributes: membersData.map((m) => getMemberAttributes(m))
  }
}

const defaultAvailabilityRuleInitialValues = (eventType, defaultRules) => ({
  id: getOwnRuleId(eventType),
  rules: getDefaultRules(),
  timezone: eventType.own_availability_rule?.timezone ?? defaultRules?.timezone
})

const getAvailabilityRulesInitialValues = (eventType, defaultRules) => ({
  activeAvailabilityRuleId: eventType.active_availability_rule_id,
  ownAvailabilityRule: eventType.own_availability_rule,
  userRuleType: eventType.active_availability_rule_id ? 'custom' : 'existing',
  customRules: eventType.own_availability_rule?.rules ?? defaultRules?.rules ?? getDefaultRules(),
  timezone: eventType.own_availability_rule?.timezone ?? defaultRules?.timezone,
  // this should not do anything for personal
  eventTypeAvailability: getAvailabilityFromRule(
    eventType.own_availability_rule || defaultAvailabilityRuleInitialValues(eventType, defaultRules)
  )
})

export const getInitialValues = ({ eventType, defaultAvailabilityRules }) => {
  const members = determineMembers(eventType, defaultAvailabilityRules.timezone)

  return ({
    ...getAvailabilityRulesInitialValues(eventType, defaultAvailabilityRules),
    ...getBookingRangeInitialValues(eventType),
    ...getNoticePeriodInitialValue(eventType),
    ...(eventType.isMETParent ? {} : buildMembershipsData(members))
  })
}

export const MEMBER_PREVIEW_AVAILABILITY_ATTRIBUTES = [
  'usingCustomHours', 'customRuleId', 'activeAvailabilityRuleId',
  'availableHours', 'timezone', 'dateOverrides',
  'usingSecondarySchedule', 'defaultHoursId'
]

const MEMBERSHIP_AVAILABILITY_ATTRIBUTES = [
  'userId', 'userUuid', 'memberId',
  'organizationInvitationId', 'organizationInvitationUuid',
  'isPending',
  'usingSecondarySchedule', 'defaultHoursId',
  'usingCustomHours', 'customRuleId', 'activeAvailabilityRuleId',
  'availableHours', 'timezone', 'dateOverrides'
]

const ADDITIONAL_ATTRIBUTES_FOR_BACKEND = ['id', 'availability_rule_reference_attributes']

export const formatEventTypeMembershipsAttributes = (values, isTeam) => {
  const membershipAvailabilityAttributes = values.event_type_memberships_attributes.map((attrs) => pick(
    attrs, [...MEMBERSHIP_AVAILABILITY_ATTRIBUTES, ...ADDITIONAL_ATTRIBUTES_FOR_BACKEND]
  ))

  const mergedMembershipAttributes = values.memberships.map((membership) => {
    const availabilityAttributes = findAvailabilityMembership(membershipAvailabilityAttributes, membership)

    return availabilityAttributes ? { ...membership, ...availabilityAttributes } : membership
  })

  const members = isTeam ? filterEnabledMemberships(mergedMembershipAttributes) : mergedMembershipAttributes

  return membersToServerDataV3(members)
}

const determineActiveAvailabilityData = (eventType, formValues) => {
  if (eventType.type === 'ManagedEventType') return null

  const { userRuleType, eventTypeAvailability, event_type_memberships_attributes: membershipAvailability } = formValues
  const missingAvailabilityFormValues = !userRuleType || !eventTypeAvailability || !membershipAvailability

  if (missingAvailabilityFormValues) return null

  const isMultiHost = eventType.pooling_type
  const { availableHours, timezone } = eventTypeAvailability

  if (isMultiHost) {
    if (!userRuleType || userRuleType === 'existing') return null

    return encodeAvailabilityRule(availableHours, [], timezone)
  }

  if (!membershipAvailability[0]) return null

  const { availableHours: membershipAvailableHours, timezone: membershipTimezone } = membershipAvailability[0]

  return encodeAvailabilityRule(membershipAvailableHours, [], membershipTimezone)
}

export const getFormatValuesToServerData = (eventType) => (values) => {
  const { isMultiHost, isTeam, isMETParent } = eventType

  const {
    periodType,
    movingType,
    startDate,
    endDate,
    maxBookingTime,
    minBookingTime,
    minBookingTimeType,
    userRuleType,
    eventTypeAvailability,
    shareOverrideAvailability
  } = values

  const { timezone: etTimezone, availableHours: etAvailableHours, dateOverrides: etDateOverrides } = eventTypeAvailability

  const attributes = {
    period_type: getPeriodType(periodType, movingType),
    start_date: startDate,
    end_date: endDate,
    max_booking_time: getMaxBookingTime(maxBookingTime),
    min_booking_time: getMinBookingTime(minBookingTime, minBookingTimeType)
  }

  if (isMultiHost || isMETParent) {
    if (userRuleType === 'custom') {
      attributes.availability_rule_attributes = {
        ...encodeAvailabilityRule(etAvailableHours, etDateOverrides, etTimezone)
      }
      attributes.availability_rule_reference_attributes = {
        availability_rule_id: getOwnRuleId(eventType)
      }

      return attributes
    }

    attributes.availability_rule_reference_attributes = {
      availability_rule_id: null
    }
  }

  if (shareOverrideAvailability) {
    attributes.shareOverrideAvailability = {
      ...(shareOverrideAvailability?.id ? { id: shareOverrideAvailability.id } : {}),
      ...encodeAvailabilityRule(
        shareOverrideAvailability.availableHours,
        shareOverrideAvailability.dateOverrides,
        shareOverrideAvailability.timezone
      )
    }
  }

  return {
    ...attributes,
    ...(isMETParent ? {} : formatEventTypeMembershipsAttributes(values, isTeam))
  }
}

const getAvailableHoursValidationSchema = () => object({
  eventTypeAvailability: object().test('validateAvailableHours', '', (value) => {
    if (!value?.availableHours || !value?.usingCustomHours) return true

    return areAvailableHoursValid(value.availableHours)
  }),
  event_type_memberships_attributes: array().test('validateMembershipsAvailableHours', '', (memberships) => {
    if (!memberships || !Array.isArray(memberships)) return true

    return memberships.every((membership) => {
      if (!membership?.availableHours || !membership?.usingCustomHours) return true
      return areAvailableHoursValid(membership.availableHours)
    })
  })
})

export const getValidationSchema = () => getAvailableHoursValidationSchema()
  .concat(getBookingRangeValidationSchema())
  .concat(getNoticePeriodValidationSchema())

const getAvailabilityRule = (eventType) => {
  if (eventType.active_availability_rule_id) {
    return {
      active_availability_rule_id: eventType.active_availability_rule_id,
      availability_rule: {
        id: getOwnRuleId(eventType),
        rules: eventType.own_availability_rule?.rules,
        timezone: eventType.own_availability_rule?.timezone
      },
      availability_rule_reference: {
        availability_rule_id: getOwnRuleId(eventType)
      }
    }
  }

  return {
    active_availability_rule_id: null,
    availability_rule_reference: {
      availability_rule_id: null
    }
  }
}

export const prepareAvailabilityValuesForPreview = (eventType, formValues) => {
  const {
    timezone: ownerTimezone,
    isUser,
    availability_timezone: availabilityTimezone
  } = eventType
  const {
    event_type_memberships_attributes: eventTypeMembershipsAttributes,
    memberships,
    userRuleType,
    eventTypeAvailability,
    activeAvailabilityRuleId
  } = formValues

  if (isUser) {
    const membership = memberships[0]
    const availabilityMembership = eventTypeMembershipsAttributes === undefined ? {}
      : findAvailabilityMembership(eventTypeMembershipsAttributes, membership) || {}

    return {
      active_availability_data: determineActiveAvailabilityData(eventType, formValues),
      availability_timezone: (availabilityMembership?.timezone
        ? availabilityMembership.timezone
        : availabilityTimezone) ?? ownerTimezone,
      ...getAvailabilityRule(eventType)
    }
  }

  if (userRuleType === undefined) {
    return {
      ...pick(eventType, ['availability_timezone']),
      ...getAvailabilityRule(eventType)
    }
  }

  if (userRuleType === 'custom') {
    const { timezone: ownAvailabilityTimezone, availableHours, dateOverrides, timezone } = eventTypeAvailability || {}

    return {
      availability_timezone: ownAvailabilityTimezone ?? ownerTimezone,
      active_availability_rule_id: activeAvailabilityRuleId ?? eventType.active_availability_rule_id,
      active_availability_data: determineActiveAvailabilityData(eventType, formValues),
      availability_rule_reference: {
        availability_rule_id: getOwnRuleId(eventType)
      },
      availability_rule: {
        id: getOwnRuleId(eventType),
        ...encodeAvailabilityRule(availableHours, dateOverrides, timezone)
      }
    }
  }

  return {
    availability_timezone: ownerTimezone,
    active_availability_data: determineActiveAvailabilityData(eventType, formValues),
    active_availability_rule_id: null,
    availability_rule_reference: {
      availability_rule_id: null
    },
  }
}


export const useAddMissingEventTypeMemberships = () => {
  const [{ value: etmAttrs = [] }] = useFormField({ name: 'event_type_memberships_attributes' })
  const defaultAvailabilityRule = useGetDefaultAvailabilityRules()


  return useCallback((memberships) => {
    if (!memberships.length) { return etmAttrs }

    const etmAttrsUserIds = etmAttrs.filter(({ userId }) => userId).map(({ userId }) => userId)
    const etmAttrsOrganizationInvitationIds = etmAttrs.filter(
      ({ organizationInvitationId }) => organizationInvitationId).map(({ organizationInvitationId }) => organizationInvitationId
    )

    const newMemberships = memberships.filter((membership) => {
      const { user_id: userId, organization_invitation_id: organizationInvitationId } = membership

      if (userId && etmAttrsUserIds.includes(userId)) { return false }
      if (organizationInvitationId && etmAttrsOrganizationInvitationIds.includes(organizationInvitationId)) { return false }

      return true
    })

    if (!newMemberships.length) { return etmAttrs }

    return [...etmAttrs, ...newMemberships.map((attributes) => {
      const fullAttributes = {
        ...attributes,
        primary_availability_rule_id: attributes.is_pending ? DEFAULT_RULE_ID : attributes.primary_availability_rule_id,
        active_availability_rule_id: attributes.is_pending ? DEFAULT_RULE_ID : attributes.primary_availability_rule_id,
        owner_availability_rules: attributes.is_pending ? null : attributes.owner_availability_rules
      }

      return getMemberAttributes(getMemberData(fullAttributes, defaultAvailabilityRule.timezone))
    })]
  }, [etmAttrs, defaultAvailabilityRule])
}
